import { useState, useEffect } from 'react';

export const C = {
  bg: '#1A2530',
  surf: '#1E2D3A',
  surf2: '#162030',
  surf3: '#243444',
  bdr: '#2E4155',
  bdr2: '#3A5268',
  navy: '#3297DB',
  navyd: '#1A7FC4',
  navybg: '#112233',
  orange: '#E84C3D',
  orangel: '#FF6A5A',
  orangebg: '#2A1510',
  steel: '#ECF0F1',
  steeld: '#BDC3C7',
  steelder: '#8A9BAA',
  txt: '#ECF0F1',
  mut: '#8FA8B8',
  dim: '#4A6278',
  dark: '#0F1A24',
};

// Responsive breakpoint hook. Mobile = viewport narrower than `breakpoint`.
export const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

export const scrollTo = (id) => {
  if (window.location.pathname !== '/') {
    window.location.assign(`/#${id}`);
    return;
  }
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  // Focus the first question once the visitor has actually chosen to go to the
  // form. Deliberately not `autoFocus` on the field itself: the form sits
  // mid-page, so focusing it at load would yank the viewport past the hero for
  // everyone who arrives from an ad. `preventScroll` keeps the smooth scroll
  // above from being cut short by the focus call.
  if (id === 'submit') {
    document.getElementById('mm-description')?.focus({ preventScroll: true });
  }
};

export const metalGradient = {
  background: 'linear-gradient(180deg, #D0D8E0 0%, #8A9BAA 25%, #ECF0F1 48%, #7A8E9C 72%, #BDC3C7 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontFamily: "'Rajdhani', sans-serif",
  fontWeight: 700,
  letterSpacing: '3px',
};

export const Logo = ({ size = 48 }) => (
  <svg width={size} height={size * 0.82} viewBox="0 0 100 82" fill="none">
    <path d="M8,78 L8,8 L22,8 L37,38 L52,8 L66,8 L66,78 L53,78 L53,52 L37,65 L21,52 L21,78 Z" fill="#0D1C2A" />
    <polygon points="0,0 8,8 22,8 14,0" fill="#4A6278" />
    <polygon points="14,0 22,8 37,38 29,30" fill="#3A5268" />
    <polygon points="29,30 37,38 52,8 44,0" fill="#3A5268" />
    <polygon points="44,0 52,8 66,8 58,0" fill="#4A6278" />
    <path d="M0,70 L0,0 L14,0 L29,30 L44,0 L58,0 L58,70 L45,70 L45,44 L29,57 L13,44 L13,70 Z" fill="#2C3F50" />
    <polygon points="29,16 21,30 29,38 37,30" fill="#3297DB" />
    <polygon points="29,16 37,30 37,38 29,30" fill="#1A6FA8" />
    <rect x="73" y="8" width="9" height="70" rx="2" fill="#0D1C2A" />
    <polygon points="64,0 73,8 82,8 73,0" fill="#4A6278" />
    <rect x="64" y="0" width="9" height="70" rx="2" fill="#2C3F50" />
    <rect x="64" y="0" width="9" height="14" rx="2" fill="#3297DB" />
  </svg>
);

export const BtnOrange = ({ children, onClick, style = {} }) => {
  return (
    <button onClick={onClick} style={{
      fontFamily: "'Instrument Sans', sans-serif",
      fontSize: 13, fontWeight: 600, letterSpacing: '0.06em',
      padding: '13px 26px', borderRadius: 5,
      border: `1.5px solid ${C.orange}`,
      background: C.orange, color: '#fff',
      transition: 'all 0.15s',
      display: 'inline-flex', alignItems: 'center', gap: 8,
      ...style,
    }}
      onMouseEnter={e => { e.currentTarget.style.background = C.orangel; e.currentTarget.style.borderColor = C.orangel; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.orange; e.currentTarget.style.borderColor = C.orange; }}
    >{children}</button>
  );
};

export const BtnNavy = ({ children, onClick, style = {} }) => (
  <button onClick={onClick} style={{
    fontFamily: "'Instrument Sans', sans-serif",
    fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
    padding: '13px 26px', borderRadius: 5,
    border: `1.5px solid ${C.navy}`,
    background: 'transparent', color: C.navy,
    transition: 'all 0.15s',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    ...style,
  }}
    onMouseEnter={e => { e.currentTarget.style.background = C.navybg; e.currentTarget.style.color = C.steel; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.navy; }}
  >{children}</button>
);

export const SectionLabel = ({ children, orange = false }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: orange ? C.orange : C.navy,
    display: 'block', marginBottom: 12,
  }}>{children}</span>
);

export const H2 = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 'clamp(28px, 5vw, 50px)',
    fontWeight: 700, lineHeight: 1.05,
    textTransform: 'uppercase', color: C.txt,
    ...style,
  }}>{children}</h2>
);

export const wrap = {
  maxWidth: 1100, margin: '0 auto', padding: '0 24px',
};
